/**
 * The agent wire vocabulary.
 *
 * The backend exposes `type`, `kind`, `status` and `agentKey` as plain `String!`
 * (graphene-django renders a choices enum as an empty object type, so it cannot
 * be queried). Codegen therefore hands us `string` for all of them. These unions
 * are where that widening stops: convert once, at the boundary, and everything
 * downstream is exhaustively checked.
 */

import type {
  Concept,
  ConceptCardData,
  FormAnswerData,
  FormCard,
  OptionsStatus,
} from "./answers/form/formTypes";
import {
  asOptionsStatus,
  parseConcept,
  parseOptions,
} from "./answers/form/parseForm";
import type { LegacyExchange } from "./legacyExchange";
import type { SelectOption } from "./wire";

/**
 * The events a client can actually receive.
 *
 * There is no `RUN_QUEUED`: a run is queued on the backend's own run queue, not on
 * this stream, and the first thing the stream says about a run is `RUN_STARTED`. The
 * subscription is a read-only view — it never claims or starts anything.
 */
export const AGENT_EVENT_TYPES = [
  "RUN_STARTED",
  "TEXT_DELTA",
  "TOOL_CALL_STARTED",
  "TOOL_CALL_FINISHED",
  "RUN_COMPLETED",
  "RUN_FAILED",
  /**
   * A background content-research job, not the run's own agent loop — it runs
   * off to the side while the run keeps talking, correlated by `jobId` rather
   * than `runId`. See `turnReducer`'s `reduceResearch`.
   */
  "RESEARCH_STARTED",
  "RESEARCH_PROGRESS",
  "RESEARCH_COMPLETED",
  "RESEARCH_FAILED",
  /**
   * Liveness only, renders nothing: the model is mid-generation on something
   * that never streams as text (long tool arguments, thinking). Exists to feed
   * the stall watchdog's `lastEventAt` during otherwise-silent stretches.
   */
  "HEARTBEAT",
  /**
   * Form-first flow. The options of a select that was computed after its card was
   * shown (the Brief's purchase driver). Belongs to a background job, not the turn.
   */
  "FORM_OPTIONS",
  /** One concept finished; they are written in parallel and stream in as they land. */
  "CONCEPT_READY",
  "CONCEPTS_COMPLETED",
  /** The script is saved on the draft; the text streamed before it was TEXT_DELTA. */
  "SCRIPT_READY",
] as const;

export type AgentEventType = (typeof AGENT_EVENT_TYPES)[number];

export type AgentMessageKind = "REQUEST" | "RESPONSE";

export type AgentRunStatus = "QUEUED" | "RUNNING" | "COMPLETED" | "FAILED";

export type AgentSessionStatus = "ACTIVE" | "ARCHIVED";

/** One event off the wire, already narrowed. */
export type AgentStreamEvent = {
  type: AgentEventType;
  /**
   * The event's position in the session's Redis stream. Null only on the snapshot of
   * a session that has no stream yet. The store drops any event not newer than the
   * last one it applied, and a resubscription sends the last one as `afterEventId`.
   */
  eventId: string | null;
  /**
   * The event describes the session's state on arrival rather than something that was
   * written to the stream. Its `eventId` is where the stream stood, not an event the
   * client has seen, so it never moves the resume cursor: resuming from it would skip
   * everything between the last real event and that point.
   */
  isSnapshot: boolean;
  runId: string;
  /**
   * RUN_STARTED | RUN_COMPLETED | RUN_FAILED: which job the run is (`driver_options`,
   * `concepts`, `script`, `chat_edit`…). Informational: the client never branches on
   * the name, an open vocabulary the backend owns — see `background`.
   */
  job: string | null;
  /**
   * RUN_STARTED | RUN_COMPLETED | RUN_FAILED: the run is background work (a form
   * field's options), never the turn the person is waiting on. False on every other
   * event.
   */
  background: boolean;
  delta: string | null;
  text: string | null;
  toolName: string | null;
  toolCallId: string | null;
  error: string | null;
  /** RESEARCH_*: which background research job. */
  jobId: string | null;
  /** RESEARCH_PROGRESS: QUEUED | SCRAPING | ANALYZING | SCORING | FALLBACK. */
  stage: string | null;
  /** RESEARCH_PROGRESS: a human (Spanish) progress message. */
  detail: string | null;
  /** RESEARCH_COMPLETED: how many outlier contents were found. */
  outlierCount: number | null;
  /** RESEARCH_COMPLETED: whether the job fell back to industry references. */
  usedFallback: boolean | null;
  /** RESEARCH_PROGRESS | RESEARCH_COMPLETED: winner thumbnail URLs (up to 6),
   * in order — previous-scrape evidence while running, definitive set on
   * completion. */
  thumbnails: string[] | null;
  /** FORM_OPTIONS: which form and which of its fields the options belong to. */
  formId: string | null;
  fieldKey: string | null;
  /**
   * FORM_OPTIONS: the product the options were computed for — a catalogue product's id
   * or a link, exactly one set. `runId` is the run that computed them.
   */
  productId: string | null;
  url: string | null;
  options: SelectOption[] | null;
  optionsStatus: OptionsStatus | null;
  /** CONCEPT_READY: the concept, how many have finished, how many were asked for. */
  concept: Concept | null;
  total: number | null;
};

/** A tool the agent called during the current turn. */
export type AgentToolCall = {
  toolCallId: string;
  toolName: string;
  done: boolean;
};

/**
 * A bubble in the transcript.
 *
 * Persisted rows whose `text` is empty are tool calls and tool returns — they are
 * kept for replay but they are not transcript, so they never become a bubble. The
 * exceptions are the cards the backend posts (a form, a concept) and the rows of an
 * old interview-style chat that carry a question and its answer.
 */
export type AgentChatMessage = {
  id: string;
  role: "user" | "assistant";
  text: string;
  createdAt: string | null;
  /** True for the local echo of a message the server has not confirmed yet. */
  pending: boolean;
  /**
   * A question row of an old interview-style chat, as the text it showed: the prompt,
   * and the answer on the row that carries one. Read-only history — nothing draws a
   * widget for it any more.
   */
  legacy: LegacyExchange | null;
  /**
   * Form-first rows. A FORM card is `form` with no `formAnswer`; the row that submitted
   * it carries both (the card it answered, and what was submitted). A concept card is
   * `concept`. At most one of `legacy`, `form` and `concept` is set — they are three
   * readings of the same `question` column, told apart by its `kind`.
   */
  form: FormCard | null;
  formAnswer: FormAnswerData | null;
  concept: ConceptCardData | null;
};

/** Narrow an event straight off the wire; unknown types are ignored, not thrown. */
export const toAgentEvent = (raw: {
  type: string;
  eventId?: string | null;
  isSnapshot?: boolean | null;
  runId: string;
  job?: string | null;
  background?: boolean | null;
  delta: string | null;
  text: string | null;
  toolName: string | null;
  toolCallId: string | null;
  error: string | null;
  jobId: string | null;
  stage: string | null;
  detail: string | null;
  outlierCount: number | null;
  usedFallback: boolean | null;
  thumbnails: string[] | null;
  formId?: string | null;
  fieldKey?: string | null;
  productId?: string | null;
  url?: string | null;
  /** JSON, still a string on the wire. */
  options?: string | null;
  optionsStatus?: string | null;
  /** JSON, still a string on the wire. */
  concept?: string | null;
  total?: number | null;
}): AgentStreamEvent | null => {
  const known = AGENT_EVENT_TYPES.find((type) => type === raw.type);
  if (!known) return null;
  return {
    type: known,
    eventId: raw.eventId ?? null,
    isSnapshot: raw.isSnapshot === true,
    runId: raw.runId,
    job: raw.job ?? null,
    background: raw.background === true,
    delta: raw.delta,
    text: raw.text,
    toolName: raw.toolName,
    toolCallId: raw.toolCallId,
    error: raw.error,
    jobId: raw.jobId,
    stage: raw.stage,
    detail: raw.detail,
    outlierCount: raw.outlierCount,
    usedFallback: raw.usedFallback,
    thumbnails: raw.thumbnails,
    formId: raw.formId ?? null,
    fieldKey: raw.fieldKey ?? null,
    productId: raw.productId ?? null,
    url: raw.url ?? null,
    options: raw.options ? parseOptions(raw.options) : null,
    optionsStatus: raw.optionsStatus
      ? asOptionsStatus(raw.optionsStatus)
      : null,
    concept: raw.concept ? parseConcept(raw.concept) : null,
    total: raw.total ?? null,
  };
};
